(function TripSummaryScheduleDay($, Adzuki)
{
	'use strict';

	// ADZ-8483. How far a component may move, read by the day list (drag) and
	// the card menu (Move), so the two cannot disagree. Laid out by Days -- an
	// Itinerary, or a Proposal set to Days -- a round-trip air, rail or
	// transfer, or a one-way with more than one leg, shows one card per leg,
	// each placed by its own date: moving a leg to another day would give it
	// that day's date and split it from its other legs. Reordering within its
	// own day changes no date, so that is allowed. In Sections a section is
	// not a date, so those may still change section. A cruise does not move.
	//
	// This used to test for an Itinerary (type 2), which left a Days proposal
	// unguarded -- and since ADZ-8479 every proposal made from Find a Cruise
	// opens in Days.
	const moveRules = {
		canReorder(component) {
			return component.component_type !== 'cruise';
		},
		canChangeDay(component, sectionMode, oneWayCounts) {
			if (!moveRules.canReorder(component)) {
				return false;
			}

			if (sectionMode) {
				return true;
			}

			if (component.component_subtype === 'round-trip' || component.component_subtype === 'round-trip-transfer') {
				return false;
			}

			const counts = (oneWayCounts ? oneWayCounts.get(component.fit_builder_component_id) : 0);

			return !(counts && counts > 1);
		},
	};

	Adzuki.TripSummaryMoveRules = moveRules;

	const component = {
		template: '#ts-schedule-day',
		emits   : [
			'add',
			'labelChanged',
			'moveDay',
			'removeDay',
			'rootDateChanged',
		],
		inject: [
			'getFaIconStyle',
			'formatDate',
			'formatTime',
		],
		props: {
			sectionMode: {
				type   : Boolean,
				default: false,
			},
			changeDates: {
				type   : Boolean,
				default: true,
			},
			componentTypes: {
				type   : Array,
				default: [],
			},
			currentDay: {
				type   : Object,
				default: {},
			},
			dayNumber: {
				type   : Number,
				default: 1,
			},
			fitBuilderTypeId: {
				type   : Number,
				default: 1,
			},
			oneWayCounts: {
				type   : Map,
				default: new Map(),
			},
			// TS-415: hide Move Up on the first day and Move Down on the last
			isFirstDay: {
				type   : Boolean,
				default: false,
			},
			isLastDay: {
				type   : Boolean,
				default: false,
			},
			// TS-417: { show, allowed, reason } from the form's dayRemovalStates
			removal: {
				type   : Object,
				default: null,
			},
		},
		data() {
			return {
				addingComponent: false,
				currentDate    : null,
				// buffered so the x/Escape can actually cancel -- the input used to
				// v-model straight onto currentDay.label, which meant every keystroke
				// was already committed to the itinerary and there was nothing to undo
				labelDraft     : '',
				showDatePicker : false,
				showDayLabel   : false,
			};
		},
		computed: {
			currentDayComponents() {
				const components = this.currentDay.components || [];

				// copy before sorting: sort() is in-place, and this array is shared
				// with ts-schedule-component-day, so sorting here would mutate
				// reactive state during render
				return components.slice().sort((a, b) => {
					let aOrder = a.sort_order || 0;
					let bOrder = b.sort_order || 0;

					return aOrder - bOrder;
				});
			},
			// TS-421: in Sections mode a section's date is its own components' dates
			// -- one date, a range, or none -- never a stored day date, which the
			// Days logic spread across sections one day apart
			headerDate() {
				if (!this.sectionMode) {
					return this.currentDay.date ? this.formatDate(this.currentDay.date) : 'No date';
				}

				const dates = (this.currentDay.components || [])
					.map((component) => (component.date ? String(component.date).slice(0, 10) : null))
					.filter((date) => date)
					.sort();

				if (!dates.length) {
					return this.currentDay.date ? this.formatDate(this.currentDay.date) : '';
				}

				const first = dates[0];
				const last  = dates[dates.length - 1];

				return (first === last)
					? this.formatDate(first)
					: moment(first).format('MMM D') + ' – ' + moment(last).format('MMM D');
			},
			containerNoun() {
				return this.sectionMode ? 'Section' : 'Day';
			},
			dayLabel() {
				return this.currentDay.label || `${this.containerNoun} ${this.dayNumber}`;
			},
		},
		methods: {
			// TS-391: the same one-liners the Add Component drawer shows; the map is
			// owned by ts_add_component.js and read lazily so load order does not matter
			typeDescription(type) {
				const descriptions = Adzuki.TripSummaryTypeDescriptions || {};

				return descriptions[type] || '';
			},
			addComponent(component) {
				this.addingComponent = true;

				const componentData     = component;
				componentData.dayNumber = this.dayNumber;
				componentData.dayDate   = this.currentDate;

				this.$emit('add', componentData);
			},
			// the row gets a grip and may be picked up (ts-drag)
			componentCanBeDragged(component) {
				if (!moveRules.canReorder(component)) {
					return false;
				}

				// ADZ-8483: a row held to its day with nothing else on the day has
				// nowhere to go, so it shows no grip rather than one that does nothing
				if (this.componentLockedToDay(component) && (this.currentDay.components || []).length < 2) {
					return false;
				}

				return true;
			},
			// ADZ-8483: the row may be dropped only back into its own day
			// (ts-drag-same-day, which ts_agent_schedule's accepts() reads)
			componentLockedToDay(component) {
				return moveRules.canReorder(component)
					&& !moveRules.canChangeDay(component, this.sectionMode, this.oneWayCounts);
			},
			dateChanged(newDate) {
				// TS-421: the picker re-emits 'changed' whenever its value is set from
				// code, including on load. In Sections mode ignore a date the section
				// already has, or the echo would move its components. Days mode keeps
				// the echo, which re-stamps each day's date onto its components.
				const current = this.currentDay.date ? String(this.currentDay.date).slice(0, 10) : '';

				if (this.sectionMode && (!newDate || newDate === current)) {
					return;
				}

				this.updateDayDate(newDate);
			},
			scrollToComponent(component) {
				const componentElement = document.getElementById(`component-${component.id}`);

				if (componentElement) {
					componentElement.scrollIntoView({
						behavior: 'smooth',
						block   : 'center'
					});
				}
			},
			scrollToDay() {
				// jump the right-hand details pane to this day (the wrapper carries
				// id="ts-day-<n>"). Works even for a day with no components yet.
				const dayElement = document.getElementById(`ts-day-${this.dayNumber}`);

				if (dayElement) {
					dayElement.scrollIntoView({
						behavior: 'smooth',
						block   : 'start'
					});

					// brief highlight so the eye catches where it landed
					dayElement.classList.add('ts-day-flash');
					setTimeout(() => dayElement.classList.remove('ts-day-flash'), 1200);
				}
			},
			toggleDatePicker() {
				this.showDatePicker = !this.showDatePicker;
			},
			cancelDayLabel() {
				this.showDayLabel = false;
				this.labelDraft   = this.currentDay.label || '';
			},
			saveDayLabel() {
				const label = this.labelDraft.trim();

				this.showDayLabel = false;

				// nothing actually changed -- don't spend a save request or a toast
				if (label === (this.currentDay.label || '')) {
					return;
				}

				// null (not '') so the dayLabel fallback and the root's dayLabels
				// payload both treat a cleared label as "no label"
				this.currentDay.label = label || null;

				this.$emit('labelChanged');
			},
			toggleDayLabel() {
				if (this.showDayLabel) {
					this.cancelDayLabel();

					return;
				}

				this.labelDraft   = this.currentDay.label || '';
				this.showDayLabel = true;

				this.$nextTick(() => {
					const input = this.$refs.dayLabelInput;

					if (input) {
						input.focus();
						input.select();
					}
				});
			},
			updateDayDate(date) {
				this.$emit('rootDateChanged', date, this.dayNumber);
				this.showDatePicker = false;
			},
		},
		watch: {
			currentDay: {
				handler(newDay) {
					if (newDay.date) {
						this.currentDate = newDay.date;
					}
				},
				deep: true,
			},
		},
		mounted() {
			// reset the quick-add spinner when the component modal opens
			this.componentModalOpenHandler = () => {
				this.addingComponent = false;
			};

			document.addEventListener('ts-component-modal-open', this.componentModalOpenHandler);

			if (this.currentDay.date) {
				this.currentDate = this.currentDay.date;
			}
		},
		beforeUnmount() {
			document.removeEventListener('ts-component-modal-open', this.componentModalOpenHandler);
		},
	};

	Adzuki.VueComponents.TripSummaryScheduleDay = component;
}(jQuery, window.Adzuki || {}));
